<template>
  <slot
    :isLoading="showInitialSkeleton"
    :hasError="hasError"
    :totalOpenInterest="totalOpenInterest"
    :formattedValue="totalOpenInterest !== null ? formatNumber(totalOpenInterest) : '&mdash;'"
  >
    <div class="text-on-surface">
      <Skeleton v-if="showInitialSkeleton" height="1rem" width="40px" animation="none" />
      <span v-else-if="hasError">-</span>
      <span v-else-if="totalOpenInterest !== null">{{ formatNumber(totalOpenInterest) }}</span>
      <span v-else class="text-on-surface">&mdash;</span>
    </div>
  </slot>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatCompactNumber } from '@/utils/formatNumber';
import { useAggOpenInterest } from '@/api/mdfarm.queries';

const props = defineProps<{
  /** "conid@exchange" pairs (from strike.yesConidExPairs) */
  conidExPairs?: string[];
  /** Or pass contracts directly with conid_yes + exchange */
  contracts?: { conid_yes: number; exchange: string }[];
  /** Seed OI value from product response (pre-aggregated) */
  seedOpenInterest?: number;
}>();

const useSeed = computed(() => props.seedOpenInterest !== undefined);

const resolvedPairs = computed(() => {
  if (useSeed.value) return [];
  if (props.conidExPairs?.length) return props.conidExPairs;
  if (props.contracts?.length) return props.contracts.map((c) => `${c.conid_yes}@${c.exchange}`);
  return [];
});

const query = useAggOpenInterest(computed(() => ({ conidExPairs: resolvedPairs.value })));

const showInitialSkeleton = computed(() => !useSeed.value && query.isFetching.value && !query.data.value);
const hasError = computed(() => !useSeed.value && query.isError.value);

const totalOpenInterest = computed(() => {
  if (useSeed.value) return props.seedOpenInterest!;
  const data = query.data.value;
  if (!data) return null;
  return data.agg;
});

const formatNumber = formatCompactNumber;
</script>
